import React, { useState, useEffect } from 'react';
import { X, Search, Sparkles, Edit2, Trash2, Check, AlertCircle, Upload, Image as ImageIcon } from 'lucide-react';
import { scryfallAPI, uploadAPI } from '../services/api';
import { useTranslation } from 'react-i18next';

const CartItemCustomizer = ({ item, onSave, onClose, existingCustomization }) => {
  const { t } = useTranslation();
  const isCustomProduct = item.product_name.toLowerCase().includes('custom');
  const isDoubleSided = item.product_name.toLowerCase().includes('double-sided');
  
  // Step 1: Card list input
  const [step, setStep] = useState(1); // 1: input list, 2: configure cards
  const [cardListInput, setCardListInput] = useState('');
  const [cards, setCards] = useState([]); // Array of card objects with selections
  
  // Modal for selecting version of a specific card
  const [selectingCardIndex, setSelectingCardIndex] = useState(null);
  const [versionSearchResults, setVersionSearchResults] = useState([]);
  const [searchingVersions, setSearchingVersions] = useState(false);
  
  // General settings
  const [frameType, setFrameType] = useState('regular');
  const [customFrameType, setCustomFrameType] = useState('');
  const [comments, setComments] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Card back settings (only for Double-Sided products)
  const DEFAULT_CARD_BACK = 'https://customer-assets.emergentagent.com/job_371c3e43-0c79-40b3-81cb-e054f2680b6f/artifacts/3voqgvz0_back4.1%20%281%29.png';
  const [cardBackUrl, setCardBackUrl] = useState(DEFAULT_CARD_BACK);
  const [uploadingCardBack, setUploadingCardBack] = useState(false);

  // Load existing customization if provided
  useEffect(() => {
    if (existingCustomization) {
      // Load card back
      if (existingCustomization.card_back_url) {
        setCardBackUrl(existingCustomization.card_back_url);
      }
      
      // Load frame type
      if (existingCustomization.frame_type) {
        const isCustomFrame = !frameTypes.find(ft => ft.value === existingCustomization.frame_type);
        if (isCustomFrame) {
          setFrameType('otros');
          setCustomFrameType(existingCustomization.frame_type);
        } else {
          setFrameType(existingCustomization.frame_type);
        }
      }
      
      // Load comments
      if (existingCustomization.comments) {
        setComments(existingCustomization.comments);
      }
      
      // Load cards list
      if (existingCustomization.cards && existingCustomization.cards.length > 0) {
        // Reconstruct card list input from cards
        const cardListText = existingCustomization.cards.map(card => card.name).join('\n');
        setCardListInput(cardListText);
        
        // Reconstruct cards array with selections
        const reconstructedCards = existingCustomization.cards.map((card, index) => ({
          id: `${index}`,
          name: card.name,
          selected: !!(card.selected_version || card.custom_image),
          selectedVersion: card.selected_version || null,
          customImage: card.custom_image || null
        }));
        
        setCards(reconstructedCards);
        // If cards are already configured, skip to step 2
        if (reconstructedCards.length > 0 && reconstructedCards.some(c => c.selected)) {
          setStep(2);
        }
      }
    }
  }, [existingCustomization]);

  const frameTypes = [
    { value: 'fullart', label: 'Fullart' },
    { value: 'regular', label: 'Regular' },
    { value: 'token', label: 'Token' },
    { value: 'land', label: 'Land' },
    { value: 'extended', label: 'Extended Art' },
    { value: 'borderless', label: 'Borderless' },
    { value: 'otros', label: t('checkout.frameOther') || 'Other' }
  ];

  // Determine card limit based on product name
  const getCardLimit = () => {
    const productName = item.product_name.toLowerCase();
    if (productName.includes('single')) return 1;
    if (productName.includes('10 pack')) return 10;
    if (productName.includes('50 pack') || productName.includes('50')) return 50;
    if (productName.includes('100 pack') || productName.includes('100') || productName.includes('commander')) return 100;
    return null; // No limit
  };

  const cardLimit = getCardLimit();

  // Count cards in current input
  const countCardsInInput = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    let total = 0;
    lines.forEach(line => {
      const trimmed = line.trim();
      const match = trimmed.match(/^(\d+)x?\s+(.+)$/i);
      if (match) {
        total += parseInt(match[1]);
      } else {
        total += 1;
      }
    });
    return total;
  };

  const currentCardCount = countCardsInInput(cardListInput);
  
  // Error state for validation
  const [validationError, setValidationError] = useState(null);

  // Process card list from textarea
  const processCardList = () => {
    setValidationError(null); // Clear previous errors
    
    const lines = cardListInput.split('\n').filter(line => line.trim());
    
    if (lines.length === 0) {
      setValidationError(t('checkout.emptyList') || 'Please enter at least one card');
      return;
    }
    
    const parsedCards = lines.map((line, index) => {
      // Parse format like "4x Lightning Bolt" or just "Lightning Bolt"
      const trimmed = line.trim();
      const match = trimmed.match(/^(\d+)x?\s+(.+)$/i);
      
      let quantity = 1;
      let cardName = trimmed;
      
      if (match) {
        quantity = parseInt(match[1]);
        cardName = match[2];
      }
      
      // Create one entry per quantity
      const entries = [];
      for (let i = 0; i < quantity; i++) {
        entries.push({
          id: `${index}-${i}`,
          name: cardName,
          selected: false,
          selectedVersion: null,
          customImage: null // For custom cards
        });
      }
      return entries;
    }).flat();
    
    // Validate card count with detailed message
    if (cardLimit && parsedCards.length !== cardLimit) {
      const difference = parsedCards.length - cardLimit;
      const differenceText = difference > 0 
        ? `${Math.abs(difference)} ${t('checkout.tooMany') || 'too many'}`
        : `${Math.abs(difference)} ${t('checkout.tooFew') || 'too few'}`;
      
      setValidationError(
        `${t('checkout.incorrectCardCount') || 'Incorrect card count'}\n\n` +
        `${t('checkout.required') || 'Required'}: ${cardLimit} ${t('checkout.cards') || 'cards'}\n` +
        `${t('checkout.youHave') || 'You have'}: ${parsedCards.length} ${t('checkout.cards') || 'cards'}\n` +
        `${t('checkout.difference') || 'Difference'}: ${differenceText}\n\n` +
        `${t('checkout.pleaseAdjust') || 'Please adjust your list to have exactly'} ${cardLimit} ${t('checkout.cards') || 'cards'}.`
      );
      return;
    }
    
    setCards(parsedCards);
    setStep(2);
  };

  // Search all versions (prints) of a specific card on Scryfall
  const searchCardVersions = async (cardName, cardIndex) => {
    setSearchingVersions(true);
    try {
      const data = await scryfallAPI.searchCards(`!"${cardName}"`);
      const results = data.data || [];
      setVersionSearchResults(results);
      
      // Auto-select the first (most recent) version as default
      if (results.length > 0 && cardIndex !== null) {
        const defaultVersion = results[0];
        const updatedCards = [...cards];
        updatedCards[cardIndex].selectedVersion = {
          card_name: defaultVersion.name,
          set_code: defaultVersion.set,
          set_name: defaultVersion.set_name,
          collector_number: defaultVersion.collector_number,
          scryfall_id: defaultVersion.id,
          image_uri: defaultVersion.image_uris?.normal || defaultVersion.image_uris?.large || '',
          art_crop_uri: defaultVersion.image_uris?.art_crop || ''
        };
        updatedCards[cardIndex].selected = true;
        setCards(updatedCards);
      }
    } catch (error) {
      console.error('Failed to search card versions:', error);
      alert(t('checkout.scryfallError') || 'Error searching card versions');
      setVersionSearchResults([]);
    } finally {
      setSearchingVersions(false);
    }
  };

  // Select a specific version for a card
  const selectVersion = (cardIndex, version) => {
    const updatedCards = [...cards];
    updatedCards[cardIndex].selectedVersion = {
      card_name: version.name,
      set_code: version.set,
      set_name: version.set_name,
      collector_number: version.collector_number,
      scryfall_id: version.id,
      image_uri: version.image_uris?.normal || version.image_uris?.large || '',
      art_crop_uri: version.image_uris?.art_crop || ''
    };
    updatedCards[cardIndex].selected = true;
    setCards(updatedCards);
    setSelectingCardIndex(null);
    setVersionSearchResults([]);
  };

  // Handle custom image upload for custom cards
  const handleCustomImageUpload = async (cardIndex, file) => {
    if (file.size > 5 * 1024 * 1024) {
      alert(t('checkout.fileTooLarge') || 'File too large (max 5MB)');
      return;
    }

    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      alert(t('checkout.invalidFileType') || 'Invalid file type');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      const updatedCards = [...cards];
      updatedCards[cardIndex].customImage = {
        file_id: response.data.file_id,
        storage_path: response.data.storage_path,
        original_filename: response.data.original_filename,
        content_type: response.data.content_type,
        size: response.data.size
      };
      updatedCards[cardIndex].selected = true;
      setCards(updatedCards);
    } catch (error) {
      console.error('Upload failed:', error);
      alert(t('checkout.uploadFailed') || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Handle card back upload
  const handleCardBackUpload = async (file) => {
    if (file.size > 5 * 1024 * 1024) {
      alert(t('checkout.fileTooLarge') || 'File too large (max 5MB)');
      return;
    }

    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      alert(t('checkout.invalidFileType') || 'Invalid file type');
      return;
    }

    setUploadingCardBack(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      // Store the file_id, we'll construct download URL in backend
      setCardBackUrl(`/api/files/${response.data.file_id}`);
    } catch (error) {
      console.error('Card back upload failed:', error);
      alert(t('checkout.uploadFailed') || 'Upload failed');
    } finally {
      setUploadingCardBack(false);
    }
  };

  // Remove a card from the list
  const removeCard = (cardIndex) => {
    setCards(cards.filter((_, index) => index !== cardIndex));
  };

  // Duplicate a card
  const duplicateCard = (cardIndex) => {
    const cardToDuplicate = { ...cards[cardIndex], id: `${cards[cardIndex].id}-dup-${Date.now()}` };
    setCards([...cards.slice(0, cardIndex + 1), cardToDuplicate, ...cards.slice(cardIndex + 1)]);
  };

  // Save configuration
  const handleSave = () => {
    // Check if all cards have versions selected
    const unselectedCards = cards.filter(card => !card.selected);
    if (unselectedCards.length > 0) {
      const confirm = window.confirm(
        `${unselectedCards.length} ${t('checkout.cardsNotConfigured') || 'cards have no version selected'}. ${t('checkout.continueAnyway') || 'Continue anyway?'}`
      );
      if (!confirm) return;
    }

    const customization = {
      cards: cards.map(card => ({
        name: card.name,
        ...(isCustomProduct ? {
          custom_image: card.customImage
        } : {
          selected_version: card.selectedVersion
        })
      })),
      ...(isCustomProduct && {
        frame_type: frameType === 'otros' ? customFrameType : frameType
      }),
      comments: comments.trim() || null,
      card_back_url: cardBackUrl
    };

    onSave(customization);
  };

  // Render Step 1: Card List Input
  if (step === 1) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-brand-surface border border-brand-border rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-brand-surface border-b border-brand-border p-6 flex items-center justify-between z-10">
            <div>
              <h2 className="text-2xl font-outfit font-semibold text-white flex items-center space-x-2">
                <Sparkles size={24} className="text-violet-500" />
                <span>{t('checkout.defineCards') || 'Define Your Cards'}</span>
              </h2>
              <p className="text-zinc-400 text-sm mt-1">{item.product_name}</p>
            </div>
            <button onClick={onClose} className="text-zinc-400 hover:text-white transition-colors">
              <X size={28} />
            </button>
          </div>

          <div className="p-6">
            <div className="bg-violet-900/20 border border-violet-600/50 rounded-lg p-4 mb-6">
              <p className="text-white text-sm font-medium mb-2">
                📝 {t('checkout.pasteDecklist') || 'Paste your decklist here'}
              </p>
              <p className="text-zinc-400 text-xs">
                {t('checkout.decklistFormat') || 'One card per line. Format: "4x Lightning Bolt" or "Lightning Bolt"'}
              </p>
              {cardLimit && (
                <div className="mt-3 bg-yellow-900/30 border border-yellow-600/50 rounded px-3 py-2">
                  <p className="text-yellow-300 text-sm font-bold">
                    ⚠ {t('checkout.requiredCards') || 'Required'}: {cardLimit} {t('checkout.cardsExactly') || 'cards exactly'}
                  </p>
                </div>
              )}
            </div>

            <textarea
              value={cardListInput}
              onChange={(e) => setCardListInput(e.target.value)}
              placeholder="1x Lightning Bolt&#10;4x Island&#10;2x Counterspell&#10;1x Sol Ring"
              rows={15}
              className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-lg px-4 py-3 font-mono text-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all resize-none mb-3"
            />

            {/* Validation Error Message */}
            {validationError && (
              <div className="mb-6 bg-red-900/20 border-2 border-red-600 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-red-600 rounded-full flex items-center justify-center text-white font-bold">
                    !
                  </div>
                  <div className="flex-1">
                    <p className="text-red-400 font-bold text-sm mb-2">
                      ❌ {t('checkout.error') || 'Error'}
                    </p>
                    <p className="text-red-300 text-sm whitespace-pre-line">
                      {validationError}
                    </p>
                  </div>
                  <button
                    onClick={() => setValidationError(null)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            )}

            {/* Real-time card counter */}
            <div className={`mb-6 p-4 rounded-lg border ${
              cardLimit && currentCardCount !== cardLimit
                ? currentCardCount > cardLimit
                  ? 'bg-red-900/20 border-red-600/50'
                  : currentCardCount < cardLimit && currentCardCount > 0
                    ? 'bg-yellow-900/20 border-yellow-600/50'
                    : 'bg-zinc-900 border-zinc-800'
                : currentCardCount === cardLimit
                  ? 'bg-green-900/20 border-green-600/50'
                  : 'bg-zinc-900 border-zinc-800'
            }`}>
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium ${
                  cardLimit && currentCardCount !== cardLimit
                    ? currentCardCount > cardLimit
                      ? 'text-red-400'
                      : currentCardCount < cardLimit && currentCardCount > 0
                        ? 'text-yellow-400'
                        : 'text-zinc-400'
                    : currentCardCount === cardLimit
                      ? 'text-green-400'
                      : 'text-zinc-400'
                }`}>
                  {t('checkout.currentCount') || 'Current count'}
                </span>
                <span className={`text-2xl font-bold ${
                  cardLimit && currentCardCount !== cardLimit
                    ? currentCardCount > cardLimit
                      ? 'text-red-400'
                      : currentCardCount < cardLimit && currentCardCount > 0
                        ? 'text-yellow-400'
                        : 'text-white'
                    : currentCardCount === cardLimit
                      ? 'text-green-400'
                      : 'text-white'
                }`}>
                  {currentCardCount} {cardLimit ? `/ ${cardLimit}` : ''}
                </span>
              </div>
              {cardLimit && currentCardCount > 0 && currentCardCount !== cardLimit && (
                <div className="mt-2 text-xs">
                  {currentCardCount > cardLimit ? (
                    <p className="text-red-400">
                      ❌ {currentCardCount - cardLimit} {t('checkout.tooMany') || 'too many cards'}
                    </p>
                  ) : (
                    <p className="text-yellow-400">
                      ⚠ {cardLimit - currentCardCount} {t('checkout.tooFew') || 'cards missing'}
                    </p>
                  )}
                </div>
              )}
              {cardLimit && currentCardCount === cardLimit && (
                <p className="text-green-400 text-xs mt-2">
                  ✓ {t('checkout.correctCount') || 'Perfect! Correct number of cards'}
                </p>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-zinc-800 text-white rounded-lg font-medium hover:bg-zinc-700 transition-all"
              >
                {t('checkout.cancel') || 'Cancel'}
              </button>
              <button
                onClick={processCardList}
                disabled={!cardListInput.trim()}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg font-bold hover:from-violet-500 hover:to-purple-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('checkout.processList') || 'Process List'} →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Step 2: Configure Each Card
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-brand-surface border border-brand-border rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-brand-surface border-b border-brand-border p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-outfit font-semibold text-white flex items-center space-x-2">
              <Sparkles size={24} className="text-violet-500" />
              <span>{t('checkout.configureCards') || 'Configure Each Card'}</span>
            </h2>
            <p className="text-zinc-400 text-sm mt-1">
              {item.product_name} • {cards.length} {t('checkout.cards') || 'cards'}
            </p>
          </div>
          <button onClick={() => setStep(1)} className="text-violet-400 hover:text-violet-300 text-sm font-medium">
            ← {t('checkout.backToList') || 'Back to List'}
          </button>
        </div>

        <div className="p-6">
          {/* Progress indicator */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-medium">
                {t('checkout.progress') || 'Progress'}
              </span>
              <span className="text-zinc-400 text-sm">
                {cards.filter(c => c.selected).length} / {cards.length} {t('checkout.configured') || 'configured'}
              </span>
            </div>
            <div className="w-full bg-zinc-800 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-violet-600 to-purple-600 h-2 rounded-full transition-all"
                style={{ width: `${(cards.filter(c => c.selected).length / cards.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Card list */}
          <div className="space-y-3 mb-6">
            {cards.map((card, index) => (
              <div
                key={card.id}
                className={`bg-zinc-900 border rounded-lg p-4 transition-all ${
                  card.selected ? 'border-violet-600' : 'border-zinc-800'
                }`}
              >
                <div className="flex items-start space-x-4">
                  {/* Preview */}
                  <div className="flex-shrink-0">
                    {card.selected ? (
                      <div className="w-24 h-32 rounded-lg overflow-hidden border-2 border-violet-500 relative">
                        {isCustomProduct && card.customImage ? (
                          <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                            <ImageIcon size={32} className="text-zinc-600" />
                          </div>
                        ) : card.selectedVersion ? (
                          <img 
                            src={card.selectedVersion.image_uri} 
                            alt={card.name}
                            className="w-full h-full object-cover"
                          />
                        ) : null}
                        <div className="absolute top-1 right-1 bg-green-600 rounded-full p-1">
                          <Check size={12} className="text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="w-24 h-32 rounded-lg bg-zinc-800 border-2 border-dashed border-zinc-700 flex items-center justify-center">
                        <AlertCircle size={24} className="text-zinc-600" />
                      </div>
                    )}
                  </div>

                  {/* Card info */}
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-1">{card.name}</h3>
                    
                    {card.selected ? (
                      <div className="space-y-1 mb-3">
                        {isCustomProduct && card.customImage ? (
                          <p className="text-green-400 text-sm">
                            ✓ {t('checkout.customImageUploaded') || 'Custom image uploaded'}
                          </p>
                        ) : card.selectedVersion ? (
                          <>
                            <p className="text-violet-400 text-sm font-medium">
                              {card.selectedVersion.set_name}
                            </p>
                            <p className="text-zinc-500 text-xs">
                              {card.selectedVersion.set_code.toUpperCase()} #{card.selectedVersion.collector_number}
                            </p>
                          </>
                        ) : null}
                      </div>
                    ) : (
                      <p className="text-yellow-500 text-sm mb-3">
                        ⚠ {t('checkout.noVersionSelected') || 'No version selected'}
                      </p>
                    )}

                    {/* Action buttons */}
                    <div className="flex space-x-2">
                      {isCustomProduct ? (
                        <label className="cursor-pointer px-4 py-2 bg-violet-600 text-white rounded-md text-sm font-medium hover:bg-violet-500 transition-all inline-flex items-center space-x-2">
                          <Upload size={16} />
                          <span>{card.selected ? t('checkout.changeImage') : t('checkout.uploadImage') || 'Upload Image'}</span>
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            onChange={(e) => e.target.files[0] && handleCustomImageUpload(index, e.target.files[0])}
                            disabled={uploading}
                            className="hidden"
                          />
                        </label>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectingCardIndex(index);
                            searchCardVersions(card.name, index);
                          }}
                          className="px-4 py-2 bg-violet-600 text-white rounded-md text-sm font-medium hover:bg-violet-500 transition-all flex items-center space-x-2"
                        >
                          <Search size={16} />
                          <span>{card.selected ? t('checkout.changeVersion') : t('checkout.selectVersion') || 'Select Version'}</span>
                        </button>
                      )}
                      
                      <button
                        onClick={() => duplicateCard(index)}
                        className="px-3 py-2 bg-zinc-800 text-zinc-400 rounded-md text-sm hover:text-white hover:bg-zinc-700 transition-all"
                        title={t('checkout.duplicate') || 'Duplicate'}
                      >
                        <Edit2 size={16} />
                      </button>
                      
                      <button
                        onClick={() => removeCard(index)}
                        className="px-3 py-2 bg-zinc-800 text-zinc-400 rounded-md text-sm hover:text-red-400 hover:bg-zinc-700 transition-all"
                        title={t('checkout.remove') || 'Remove'}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* General settings - ONLY show frame type for Custom products */}
          <div className="space-y-4 pt-6 border-t border-zinc-800">
            {isCustomProduct && (
              <>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">
                    {t('checkout.frameType') || 'Frame Type'} ({t('checkout.allCards') || 'for all cards'})
                  </label>
                  <select
                    value={frameType}
                    onChange={(e) => setFrameType(e.target.value)}
                    className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all"
                  >
                    {frameTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {frameType === 'otros' && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">
                      {t('checkout.specifyFrameType') || 'Specify Frame Type'}
                    </label>
                    <input
                      type="text"
                      value={customFrameType}
                      onChange={(e) => setCustomFrameType(e.target.value)}
                      placeholder={t('checkout.frameTypePlaceholder') || 'e.g., Etched foil, Showcase'}
                      className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all"
                    />
                  </div>
                )}
              </>
            )}

            {/* Card Back Selection - ONLY for Double-Sided products */}
            {isDoubleSided && (
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  🎴 {t('checkout.cardBack') || 'Card Back'} ({t('checkout.allCards') || 'for all cards'})
                </label>
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                  <div className="flex items-start space-x-4">
                    {/* Preview */}
                    <div className="flex-shrink-0">
                      <div className="w-32 h-44 rounded-lg overflow-hidden border-2 border-violet-500 bg-zinc-800">
                        <img 
                          src={cardBackUrl} 
                          alt="Card Back Preview"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = DEFAULT_CARD_BACK;
                          }}
                        />
                      </div>
                      <p className="text-xs text-zinc-500 mt-2 text-center">
                        {t('checkout.recommendedSize') || 'Recommended'}: 63 x 88 mm
                      </p>
                    </div>

                    {/* Upload button */}
                    <div className="flex-1">
                      <p className="text-zinc-300 text-sm mb-3">
                        {cardBackUrl === DEFAULT_CARD_BACK 
                          ? (t('checkout.defaultCardBack') || 'Using default card back. Upload a custom design if desired.')
                          : (t('checkout.customCardBackUploaded') || '✓ Custom card back uploaded')
                        }
                      </p>
                      
                      <label className="inline-flex items-center space-x-2 px-4 py-2 bg-violet-600 text-white rounded-md text-sm font-medium hover:bg-violet-500 transition-all cursor-pointer">
                        <Upload size={16} />
                        <span>
                          {uploadingCardBack 
                            ? (t('checkout.uploading') || 'Uploading...')
                            : (t('checkout.uploadCustomBack') || 'Upload Custom Back')
                          }
                        </span>
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          className="hidden"
                          disabled={uploadingCardBack}
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleCardBackUpload(e.target.files[0]);
                            }
                          }}
                        />
                      </label>

                      {cardBackUrl !== DEFAULT_CARD_BACK && (
                        <button
                          onClick={() => setCardBackUrl(DEFAULT_CARD_BACK)}
                          className="ml-2 px-3 py-2 bg-zinc-800 text-zinc-400 rounded-md text-sm hover:text-white hover:bg-zinc-700 transition-all"
                        >
                          {t('checkout.resetToDefault') || 'Reset to Default'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                {t('checkout.additionalComments') || 'Additional Comments'} ({t('checkout.forThisProduct') || 'for this product'})
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder={t('checkout.commentsPlaceholder') || 'Any specific instructions...'}
                rows={3}
                className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all resize-none"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 mt-6">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-zinc-800 text-white rounded-lg font-medium hover:bg-zinc-700 transition-all"
            >
              {t('checkout.cancel') || 'Cancel'}
            </button>
            <button
              onClick={handleSave}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg font-bold hover:from-violet-500 hover:to-purple-500 transition-all"
            >
              {t('checkout.saveCustomization') || 'Save Customization'}
            </button>
          </div>
        </div>
      </div>

      {/* Version Selection Modal */}
      {selectingCardIndex !== null && (
        <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4">
          <div className="bg-brand-surface border border-brand-border rounded-xl max-w-5xl w-full max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-brand-surface border-b border-brand-border p-6 flex items-center justify-between z-10">
              <div>
                <h3 className="text-xl font-outfit font-semibold text-white">
                  {t('checkout.selectVersionFor') || 'Select Version for'}: {cards[selectingCardIndex].name}
                </h3>
                <p className="text-zinc-400 text-sm mt-1">
                  {searchingVersions ? t('checkout.searching') : `${versionSearchResults.length} ${t('checkout.versionsFound') || 'versions found'}`}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectingCardIndex(null);
                  setVersionSearchResults([]);
                }}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              {searchingVersions ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-600 mx-auto mb-4"></div>
                  <p className="text-zinc-400">{t('checkout.searchingVersions') || 'Searching versions...'}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {versionSearchResults.map((version) => {
                    const imageUri = version.image_uris?.normal || version.image_uris?.large;
                    if (!imageUri) return null;

                    return (
                      <div
                        key={version.id}
                        onClick={() => selectVersion(selectingCardIndex, version)}
                        className="cursor-pointer group"
                      >
                        <div className="relative rounded-lg overflow-hidden border-2 border-transparent hover:border-violet-500 transition-all">
                          <img
                            src={imageUri}
                            alt={version.name}
                            className="w-full h-auto group-hover:scale-105 transition-transform"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="absolute bottom-0 left-0 right-0 p-3">
                              <p className="text-white font-semibold text-sm">{version.set_name}</p>
                              <p className="text-zinc-300 text-xs">
                                {version.set.toUpperCase()} #{version.collector_number}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartItemCustomizer;
